/**
 * Game Lobby Component
 * VOY-11: Pre-game screen with mode selection and boosters
 */

import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Zap } from 'lucide-react';
import { GamePool, Booster, LeaderboardEntry, GameMode, GameModeInfo, getTierEmoji } from '@/data/gaming-mockup';

interface GameLobbyProps {
  pool: GamePool;
  availableBoosters: Booster[];
  gameModes: GameModeInfo[];
  topPlayers: LeaderboardEntry[];
  yourBestScore: number;
  yourBestRank: number;
  attemptsLeft: number;
  onStartGame: (mode: GameMode) => void;
}

export function GameLobby({
  pool,
  availableBoosters,
  gameModes,
  topPlayers,
  yourBestScore,
  yourBestRank,
  attemptsLeft,
  onStartGame
}: GameLobbyProps) {
  const tierEmoji = getTierEmoji(pool.tier);

  return (
    <div className="space-y-6">
      {/* Pool Confirmation */}
      <Card className="bg-gradient-to-r from-[#5B6FED] to-[#8B9FED] text-white border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-3xl">{tierEmoji}</span>
                <h2 className="text-2xl font-bold capitalize">{pool.tier} Pool</h2>
              </div>
              <p className="text-white/90">Prize: {pool.prizes[0].description}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-white/80">Your Entry</p>
              <p className="text-xl font-bold">✅ Confirmed</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Choose Game Mode */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 dark:text-white">
          <Zap className="w-5 h-5 text-[#5B6FED] dark:text-blue-400" />
          Choose Game Mode
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {gameModes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => onStartGame(mode.id)}
              className="bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl rounded-3xl p-4 border-2 border-white/30 dark:border-zinc-700/50 hover:border-[#5B6FED] dark:hover:border-blue-500 hover:shadow-lg transition-all text-left group shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl"
            >
              <div className="flex items-start gap-3">
                <span className="text-3xl">{mode.icon}</span>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-[#5B6FED] dark:group-hover:text-blue-400 transition-colors">
                    {mode.name}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-zinc-300 mt-1">{mode.description}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-zinc-400">
                    <span>⏱️ {mode.estimatedTime}</span>
                    <span>•</span>
                    <span>{mode.difficulty}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Your Boosters */}
      <div>
        <h3 className="text-lg font-semibold mb-3 dark:text-white">🚀 Your Boosters ({availableBoosters.length} available)</h3>
        <div className="grid grid-cols-2 gap-3">
          {availableBoosters.map((booster) => (
            <div
              key={booster.id}
              className="bg-blue-50 dark:bg-zinc-700/50 rounded-xl p-3 border border-blue-200 dark:border-zinc-600"
            >
              <div className="flex items-start gap-2">
                <span className="text-2xl">{booster.icon}</span>
                <div>
                  <p className="font-semibold text-sm text-gray-900 dark:text-white">{booster.name}</p>
                  <p className="text-xs text-gray-600 dark:text-zinc-300 mt-1">{booster.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-zinc-400 mt-2">
          💡 Use 1 booster per question. More boosters unlock as you play!
        </p>
      </div>

      {/* Current Leaderboard */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 dark:text-white">
          <Trophy className="w-5 h-5 text-[#5B6FED] dark:text-blue-400" />
          Current Leaderboard
        </h3>
        <Card>
          <CardContent className="p-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {topPlayers.map((player) => (
                <div
                  key={player.rank}
                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700/50"
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-bold text-lg ${
                      player.rank === 1 ? 'text-yellow-500' :
                      player.rank === 2 ? 'text-gray-400 dark:text-zinc-400' :
                      player.rank === 3 ? 'text-amber-700 dark:text-amber-500' :
                      'text-gray-400 dark:text-zinc-400'
                    }`}>
                      {player.rank === 1 ? '🥇' : player.rank === 2 ? '🥈' : player.rank === 3 ? '🥉' : `#${player.rank}`}
                    </span>
                    <div>
                      <p className="font-medium text-sm dark:text-white">{player.username}</p>
                      {player.prize && (
                        <p className="text-xs text-gray-500 dark:text-zinc-400">{player.prize}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#5B6FED]">{player.score.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{player.attempts} attempt{player.attempts > 1 ? 's' : ''}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Your Best */}
            {yourBestScore > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="bg-purple-50 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#5B6FED]">Your Best Score</p>
                    <p className="text-xs text-gray-600">Rank #{yourBestRank}</p>
                  </div>
                  <p className="text-xl font-bold text-[#5B6FED]">{yourBestScore.toLocaleString()}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attempts Remaining */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-yellow-900">Attempts Remaining</p>
            <p className="text-xs text-yellow-700">You can improve your score</p>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  i < attemptsLeft 
                    ? 'bg-yellow-500 text-white' 
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {i + 1}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

